/**
 * Per-user Grokbot preferences (theme, in-app notification switches, last look),
 * loaded once for every page under /dashboard/bot-ops. Outside the provider the
 * hook answers "no preferences" so a component can still render on its own.
 */

import React from 'react';
import { getPrefs, markAllSeen, savePrefs } from '../api/botOpsService';
import { Prefs } from '../lib/types';
import { useQuery } from './useQuery';

export interface PrefsApi {
  prefs: Prefs | null;
  error: string | null;
  update: (patch: { theme?: 'light' | 'dark' | null; notify?: Partial<Prefs['notify']> }) => Promise<boolean>;
  markSeen: () => Promise<boolean>;
  reload: () => void;
}

const OFF: PrefsApi = { prefs: null, error: null, update: async () => false, markSeen: async () => false, reload: () => undefined };
const Ctx = React.createContext<PrefsApi>(OFF);

export const usePrefs = (): PrefsApi => React.useContext(Ctx);

export function PrefsProvider({ children }: { children: React.ReactNode }) {
  const q = useQuery(getPrefs, []);
  const [local, setLocal] = React.useState<Prefs | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const reloadQ = q.reload;
  React.useEffect(() => { if (q.data) setLocal(q.data); }, [q.data]);

  const update = React.useCallback<PrefsApi['update']>(async (patch) => {
    // Show the change now; the server's answer replaces it, and a failure puts the old value back.
    let before: Prefs | null = null;
    setLocal((cur) => {
      before = cur;
      return cur ? { ...cur, theme: patch.theme === undefined ? cur.theme : patch.theme, notify: { ...cur.notify, ...(patch.notify || {}) } } : cur;
    });
    setError(null);
    try {
      const r = await savePrefs(patch);
      setLocal(r.data);
      return true;
    } catch (e) {
      setLocal(before);
      setError((e as Error).message || 'Could not save');
      return false;
    }
  }, []);

  const markSeen = React.useCallback(async () => {
    try { const r = await markAllSeen(); setLocal(r.data); return true; } catch (e) { setError((e as Error).message || 'Could not save'); return false; }
  }, []);

  const api = React.useMemo<PrefsApi>(() => ({ prefs: local, error: error || (q.error && !local ? q.error.message : null), update, markSeen, reload: reloadQ }), [local, error, q.error, update, markSeen, reloadQ]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}
